import React, { useRef } from 'react';
import { navigate } from 'gatsby';
import SmartCheckout from '../SmartCheckout/SmartCheckout';
import { useCheckout } from '../../hooks/useCheckout';
import { useRecharge } from '../../hooks/useRecharge';
import {
  flattenEventData,
  buildCheckoutLocation,
} from '../../lib/tracking/utils';
import { normalizePaymentMethod } from '../../utils/formatters';
import {
  trackAnalyticsChoosePayment,
  trackAnalyticsSelectPayment,
  trackAnalyticsCheckoutPaymentResult,
  trackAnalyticsPixCopy,
  trackAnalyticsFallbackView,
  trackAnalyticsFallbackSuggestion,
  trackAnalyticsInCompleteRecharge,
} from '../../lib/tracking/events/recharge';

function CheckoutSDKListener() {
  const {
    sdkSessionActive,
    iframeCheckoutActive,
    setSdkSessionActive,
    setCheckoutEventData,
    sendLoopEvent,
    isSdkCheckoutEnabled,
    isCheckoutActive,
    setRecurrenceOptinStatus,
  } = useCheckout();

  const baseLocation = buildCheckoutLocation(
    isSdkCheckoutEnabled,
    isCheckoutActive,
    null,
  );
  const pixLocation = buildCheckoutLocation(
    isSdkCheckoutEnabled,
    isCheckoutActive,
    'pix',
  );
  const { selectedRechargeValue, sendRechargeIntention } = useRecharge();

  const canRequestPixIntention = useRef(true);

  const handlePaymentResult = (data: Record<string, unknown>) => {
    trackAnalyticsCheckoutPaymentResult(selectedRechargeValue, data);
    setCheckoutEventData(data);
    setSdkSessionActive(false);
    navigate('/web/resultado-checkout');
  };

  const handleClose = () => {
    sendLoopEvent('closemodal', { sdk_loaded: true });
    // Previne vazamento de sdkSessionActive: se o user fecha o fluxo SDK sem
    // concluir, a flag precisava voltar a false, senão um próximo fluxo iframe
    // (/web/pagamento-checkout) rodaria com 2 listeners → GA4 duplicado.
    setSdkSessionActive(false);
  };

  const handleClick = (data: Record<string, unknown>) => {
    const {
      action,
      payment_method: paymentMethod,
      recurrence_status: recurrenceStatus,
    } = data;
    if (action === 'copy_pix') {
      if (!canRequestPixIntention.current) return;
      sendRechargeIntention().catch(() => {});
      canRequestPixIntention.current = false;
      trackAnalyticsPixCopy(pixLocation, selectedRechargeValue);
    }
    if (action === 'payment_request') {
      // Única fonte do aceite da programada no caminho do SDK, que encerra o
      // fluxo no `payment_state` e nunca chega a receber o evento terminal.
      setRecurrenceOptinStatus((recurrenceStatus as string) ?? null);
      // O GA4 do Pagar leva o meio de pagamento no sufixo do `location`, no
      // padrão da tela de sucesso. A ordem importa: o aceite gravado acima é
      // lido pelo tracking para montar o label.
      const paymentType = normalizePaymentMethod(paymentMethod as string);
      trackAnalyticsSelectPayment(
        selectedRechargeValue,
        paymentType,
        buildCheckoutLocation(
          isSdkCheckoutEnabled,
          isCheckoutActive,
          paymentType,
        ),
      );
    }
  };

  const handlePageview = (data: Record<string, unknown>) => {
    if (data.page_name === 'checkout') {
      trackAnalyticsChoosePayment(selectedRechargeValue, baseLocation);
    }
    if (data.page_name === 'fallback' || data.page_name === 'error') {
      // A tentativa de pagamento falhou e o checkout vai oferecer outro meio.
      // O aceite da programada pertencia a ESTA tentativa e morre junto com
      // ela. Sem isso, um aceite dado no cartão sobreviveria até a conclusão
      // por Pix — que não emite `payment_request` e portanto nunca sobrescreve
      // o valor guardado — e consultaríamos a adesão de uma recarga que sequer
      // ofertou programada, recebendo 4xx e avisando de uma falha inexistente.
      setRecurrenceOptinStatus(null);
      const paymentMethod = data.payment_method as string;
      const failedLocation = buildCheckoutLocation(
        isSdkCheckoutEnabled,
        isCheckoutActive,
        paymentMethod,
      );
      const fallbackLabel =
        paymentMethod === 'credit' || paymentMethod === 'debit'
          ? 'erro_fallback_pagar_com_pix'
          : 'erro_fallback_pagar_com_credito';
      trackAnalyticsInCompleteRecharge(
        selectedRechargeValue,
        paymentMethod,
        'nao-conseguimos-concluir-seu-pagamento',
      );
      trackAnalyticsFallbackView(
        fallbackLabel,
        failedLocation,
        paymentMethod,
        (selectedRechargeValue as { id?: number } | undefined)?.id,
      );
    }
    if (data.has_fallback && data.fallback_method) {
      const suggestionLabel =
        (data.fallback_method as string) === 'pix'
          ? 'erro_fallback_pagar_com_pix'
          : 'erro_fallback_pagar_com_credito';
      trackAnalyticsFallbackSuggestion(suggestionLabel, baseLocation);
    }
  };

  const handlePixkeyImpression = () => {
    trackAnalyticsSelectPayment(selectedRechargeValue, 'pix', pixLocation);
  };

  const handleLoopEvent = (eventData?: Record<string, unknown>) => {
    sendLoopEvent(
      'postMessage:checkout:received',
      flattenEventData(eventData ?? {}),
    );
  };

  return (
    <SmartCheckout
      enabled={sdkSessionActive && !iframeCheckoutActive}
      onConfirmed={handlePaymentResult}
      onDenied={handlePaymentResult}
      onError={handlePaymentResult}
      onFailed={handlePaymentResult}
      onPending={handlePaymentResult}
      onClose={handleClose}
      onClick={handleClick}
      onPageview={handlePageview}
      onPixkeyImpression={handlePixkeyImpression}
      onLoopEvent={handleLoopEvent}
    />
  );
}

export default CheckoutSDKListener;
